import { useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  FILA_REEMBOLSOS_PLATAFORMA,
  FilaReembolsosPlataformaData,
  FilaReembolsosPlataformaVars,
} from '../services/pedidoReembolsoService';
import type { PedidoReembolso, StatusReembolso } from '../types/pedidoReembolso';
import type { PeriodoFinanceiro } from '../types/financeiro';

// AC-6 (plano AC-A3) -- período vem por parâmetro (dono é o hub/aba). Filtro local de status +
// paginação. UMA ÚNICA useQuery, `fetchPolicy: 'no-cache'` -- deliberadamente SEM as 4 queries
// extras de useReembolsosLiga.ts:68-97 (contraste documentado no plano da Phase 5): aqui
// `filaReembolsosPlataforma.contadores` já vem pronto do servidor, então `contagens` é lido
// DIRETO desse campo, nunca `.filter`/`.reduce` sobre `items` da página carregada.

export interface PedidoReembolsoComIdade extends PedidoReembolso {
  idadeDias: number;
}

export interface ContagemStatusReembolsoPlataforma {
  status: string;
  total: number;
}

export interface UseFilaReembolsosPlataformaResult {
  pedidos: PedidoReembolsoComIdade[];
  contagens: ContagemStatusReembolsoPlataforma[];
  totalItems: number;
  page: number;
  setPage: (page: number) => void;
  filtroStatus: StatusReembolso | null;
  setFiltroStatus: (status: StatusReembolso | null) => void;
  loading: boolean;
  error: Error | undefined;
  refetch: () => void;
}

export function useFilaReembolsosPlataforma(
  periodo: PeriodoFinanceiro,
): UseFilaReembolsosPlataformaResult {
  const [filtroStatus, setFiltroStatus] = useState<StatusReembolso | null>(null);
  const [page, setPage] = useState(0);

  const query = useQuery<FilaReembolsosPlataformaData, FilaReembolsosPlataformaVars>(
    FILA_REEMBOLSOS_PLATAFORMA,
    {
      variables: {
        status: filtroStatus ? [filtroStatus] : undefined,
        inicio: periodo.inicio,
        fim: periodo.fim,
        page,
        size: 10,
      },
      fetchPolicy: 'no-cache',
    },
  );

  const conexao = query.data?.filaReembolsosPlataforma ?? null;
  const items = conexao?.items ?? [];

  // Ordem preservada exatamente como devolvida pelo servidor (mais antigo primeiro,
  // idadeDias) -- nenhum sort/reorder no cliente.
  const pedidos: PedidoReembolsoComIdade[] = items.map((item) => ({
    ...item.pedido,
    idadeDias: item.idadeDias,
  }));

  // `contadores` lido DIRETO do campo do servidor -- nunca `.filter`/`.reduce` sobre `items`.
  const contagens: ContagemStatusReembolsoPlataforma[] = (conexao?.contadores ?? []).map((c) => ({
    status: c.status,
    total: c.quantidade,
  }));

  const totalItems = conexao?.totalItems ?? 0;

  return {
    pedidos,
    contagens,
    totalItems,
    page,
    setPage,
    filtroStatus,
    setFiltroStatus,
    loading: query.loading,
    error: query.error,
    refetch: () => {
      query.refetch();
    },
  };
}
